/**
 * Custom hook for managing building placement mode and preview.
 * Connects build mode state with Phaser scene.
 */

import { useEffect, useState } from 'react';
import { useBuildModeStore } from '@shared/stores/buildModeStore';
import { BuildingType } from '@shared/constants/buildings';

interface UseBuildingPlacementOptions {
  scene: unknown | null;
  hoveredTile: { x: number; y: number } | null;
}

/**
 * Hook for managing building placement preview and feedback.
 */
export function useBuildingPlacement({ scene, hoveredTile }: UseBuildingPlacementOptions) {
  const { isBuildModeActive, selectedBuildingType } = useBuildModeStore();
  const [previewState, setPreviewState] = useState<{
    buildingType: BuildingType | null;
    isValid: boolean;
    validationReason: string | undefined;
    position: { x: number; y: number };
  }>({
    buildingType: null,
    isValid: false,
    validationReason: undefined,
    position: { x: 0, y: 0 },
  });

  useEffect(() => {
    if (!scene || typeof scene !== 'object' || scene === null) return;

    const sceneWithMethods = scene as {
      updateBuildingPreview?: (
        buildingType: BuildingType | null,
        tile: { x: number; y: number } | null
      ) => void;
      getBuildingPlacementController?: () => {
        getPreviewState: () => {
          buildingType: BuildingType | null;
          isValid: boolean;
          validationResult: { reason?: string } | null;
          position: { x: number; y: number } | null;
        };
      } | null;
      input?: {
        activePointer: { x: number; y: number };
      };
    };

    if (sceneWithMethods.updateBuildingPreview) {
      if (isBuildModeActive && selectedBuildingType && hoveredTile) {
        sceneWithMethods.updateBuildingPreview(selectedBuildingType, hoveredTile);

        // Get preview state for feedback component
        const controller = sceneWithMethods.getBuildingPlacementController?.();
        if (controller) {
          const state = controller.getPreviewState();
          // Use mouse position from scene's input for accurate feedback placement
          const pointer = sceneWithMethods.input?.activePointer;
          setPreviewState({
            buildingType: state.buildingType,
            isValid: state.isValid,
            validationReason: state.validationResult?.reason,
            position: pointer ? { x: pointer.x, y: pointer.y } : { x: 0, y: 0 },
          });
        }
      } else {
        sceneWithMethods.updateBuildingPreview(null, null);
        setPreviewState({
          buildingType: null,
          isValid: false,
          validationReason: undefined,
          position: { x: 0, y: 0 },
        });
      }
    }
  }, [scene, isBuildModeActive, selectedBuildingType, hoveredTile]);

  return {
    previewState,
  };
}
